/**
 * Matching Engine Step Result Types
 *
 * Unified persistence shape for every pipeline step output. A step emits one
 * or more `MatchingEngineStepResult` docs; the orchestrator persists
 * them via `dal.matchingEngineStepResults.insertMany(results)`. The `type`
 * field is a literal discriminator that selects the variant schema.
 *
 * Adding a new step output:
 *   1. Define the variant schema (z.object with type literal, the shared base
 *      fields, and a variant-specific `data` payload).
 *   2. Append the variant to `MatchingEngineStepResultSchema` and
 *      `STEP_RESULT_TYPES`.
 *   3. Have the step fn return `readonly StepVariant[]` — heterogeneous across
 *      variants is fine (per-participant + event-wide can be produced in one
 *      step call and persisted together).
 *
 * Writes go through the closed discriminated union — no way to write a doc
 * with an unknown `type` at runtime. Read leniency lives at the persistence
 * boundary (see matching-engine-step-result.mapper.ts) so stale rows from
 * removed variants do not poison queries that care about a different, still
 * valid variant.
 *
 * `version` is write-only for now (hardcoded `CURRENT_STEP_RESULT_VERSION`);
 * it exists so a future migration can target specific doc generations.
 *
 * === Envelope v2 (additive, all optional) ===
 *
 * Every variant gains four optional envelope fields plus a nullable `data`:
 *   - `status?: "running" | "succeeded" | "failed"` — terminal state for
 *     finalized docs; "running" is reserved for placeholder docs that a
 *     subsequent commit (Track A lifecycle) writes when a step starts and
 *     amends to "succeeded"/"failed" when it ends.
 *   - `errorMessage?: string` — failure detail for `status: "failed"` docs.
 *   - `stats?: StepResultStats` — wall-clock timing + LLM accounting
 *     (totalMs, per-model token counts and LLM wall-clock).
 *   - `updatedAt?: Date` — last write timestamp; for finalized docs equals
 *     `createdAt`, for placeholder docs advances on each amend.
 *   - `data: T | null` — `null` is reserved for placeholder docs whose work
 *     is in flight or failed before producing terminal output. Today every
 *     writer still produces non-null data; readers add narrow guards (commit
 *     2 of PLA-824) so future placeholder docs (commit 4) deserialize.
 *
 * Existing readers that walk `doc.data` skip null-data docs early so they
 * only see terminal state.
 */

import { z } from "zod";
import {
  MatchingPipelineRunIdSchema,
  EventIdSchema,
  PersonIdSchema,
  type PersonId,
} from "./branded-id.schemas.js";
import { MATCHING_PIPELINE_PHASES } from "../types/admin-snapshot.types.js";
import { LocaleCodeSchema } from "./locale.schemas.js";
import { PersonPoolSchema } from "./candidate-pool-data.schemas.js";
import { PersonCoreBriefSchema } from "./person-core-brief.schemas.js";
import { ClusterMembershipDataSchema } from "./cluster-membership.schemas.js";
import { ClusterThemeDesignDataSchema } from "./cluster-theme-design.schemas.js";
import {
  MatchSelectionSchema,
  PickSetSchema,
  PickWhyConnectParagraphEntrySchema,
} from "./pick-data.schemas.js";

export const CURRENT_STEP_RESULT_VERSION = 4;

const StepResultStatusSchema = z.enum({
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
});
export type StepResultStatus = z.infer<typeof StepResultStatusSchema>;

/**
 * Enum-like constant for `StepResultStatus` so callers can compare against named values (e.g. `StepResultStatus.SUCCEEDED`) instead of raw string literals — required by the `clean-architecture-enum-vs-string-literal` lint rule.
 */
export const StepResultStatus = StepResultStatusSchema.enum;

/**
 * Wall-clock timing + LLM accounting for a single step-result.
 *
 * - `startedAt`: stamped when work begins (or at construction time for
 *   finalized docs that complete synchronously inside one writer call).
 * - `completedAt` / `totalMs`: present on terminal docs only; placeholder
 *   docs leave them undefined until the finalize amend.
 * - `tokens`: present on docs whose work touched the LLM. The per-model
 *   bucket carries that model's token counts, its `llmMs` wall-clock and its
 *   call counters, so the snapshot read-time rollup sums across mixed-model
 *   runs without losing per-model granularity. A bucket can report zero tokens
 *   and non-zero counters: an attempt that threw was still a call.
 * - `batchSize`: number of participants sharing this batch's LLM call(s)
 *   (PLA-863). Surfaced so cost-summing readers can divide the batch's
 *   `tokens` by `batchSize` to estimate per-participant cost — every
 *   participant in the batch carries the same `tokens` blob (the call's
 *   actual usage) but only contributes `1/batchSize` of it to a sum.
 *   Optional because legacy v1/v2 docs predate the field; readers default
 *   absent values to 1 (single-participant call → no division).
 */
export const StepResultStatsSchema = z.object({
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().optional(),
  totalMs: z.number().optional(),
  tokens: z
    .object({
      byModel: z.record(
        z.string(),
        z.object({
          input: z.number(),
          output: z.number(),
          // Defaulted, not required: every doc written before PLA-1850 has a
          // bucket without it.
          llmMs: z.number().default(0),
          // Defaulted, not required: every doc written before PLA-1850 has a
          // bucket without it.
          calls: z.number().default(0),
          errors: z.number().optional(),
          provider429s: z.number().optional(),
          infra429s: z.number().optional(),
          cacheRead: z.number().optional(),
          cacheWrite: z.number().optional(),
          // Proxy whole-response cache mirrors of `input`/`output` (PLA-863).

          proxyInput: z.number().optional(),
          proxyOutput: z.number().optional(),
        }),
      ),
    })
    .optional(),
  batchSize: z.number().optional(),
});
export type StepResultStats = z.infer<typeof StepResultStatsSchema>;

/**
 * Envelope v2 fields shared across every variant. Spread into each variant's z.object so the discriminated union remains a flat shape (z.discriminatedUnion does not allow nested discriminators).
 */
const envelopeV2OptionalFields = {
  status: StepResultStatusSchema.optional(),
  errorMessage: z.string().optional(),
  stats: StepResultStatsSchema.optional(),
  updatedAt: z.coerce.date().optional(),
  targetId: z.string().optional(),
  hash: z.string().optional(),
  // Absent on legacy docs.
  phase: z.enum(MATCHING_PIPELINE_PHASES).optional(),
  /**
   * The producing step definition's `codeVersion`. Distinct from `version`, which
   * is this envelope's data-schema version. Optional because docs predating the
   * field — and seeded/demo docs — carry none, so a reader must bucket absent with 0.
   */
  codeVersion: z.number().optional(),
} as const;

const envelopeBaseFields = {
  id: z.string(),
  version: z.number(),
  runId: MatchingPipelineRunIdSchema,
  eventId: EventIdSchema,
  createdAt: z.coerce.date(),
  ...envelopeV2OptionalFields,
} as const;

export const STEP_RESULT_TYPES = [
  "personCoreBrief",
  "markerParticipant",
  "personEmbeddings",
  "candidatePool",
  "clusterMembership",
  "clusterThemeDesign",
  "pick",
  "pickMatchSelection",
  "pickConnectionCritique",
  "pickSlate",
  "pickWhyConnectParagraph",
  "pickParagraphCritique",
] as const;
export type StepResultType = (typeof STEP_RESULT_TYPES)[number];

/**
 * The step types whose documents name no person: one doc per run, describing the
 * cohort rather than a participant. `satisfies` pins each entry to a variant that
 * really has no `personId`, so a type listed here by mistake fails to compile.
 */
export const COHORT_STEP_RESULT_TYPES = [
  "clusterThemeDesign",
] as const satisfies readonly PersonlessStepResultType[];

export type CohortStepResultType = (typeof COHORT_STEP_RESULT_TYPES)[number];

export const personCoreBriefStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("personCoreBrief"),
  personId: PersonIdSchema,
  data: PersonCoreBriefSchema.nullable(),
});

export type PersonCoreBriefStepResult = Readonly<
  z.infer<typeof personCoreBriefStepResultSchema>
>;

export const markerParticipantStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("markerParticipant"),
  personId: PersonIdSchema,
  data: z.array(z.string()).nullable(),
});

export type MarkerParticipantStepResult = Readonly<
  z.infer<typeof markerParticipantStepResultSchema>
>;

/**
 * Which text a vector was taken from. Every vector carries its own slot, so the
 * set is addressed one way and a text that produced no vector is simply absent —
 * no empty-string placeholder standing in for a hole, and no index shifting when
 * one is skipped.
 */
export const VectorSlotSchema = z.discriminatedUnion("kind", [
  /** Into `personCoreBrief.data.reasonsToMeet` — an identity, not a rank (PLA-1786). */
  z.object({ kind: z.literal("reason"), index: z.number().int() }),
  /** The person's own words about why they came and who they want to meet. */
  z.object({
    kind: z.literal("stated"),
    field: z.enum(["motivation", "wantsToMeet"]),
  }),
  /** Into `assumptions.items` — what we inferred, not what they said. */
  z.object({ kind: z.literal("assumption"), index: z.number().int() }),
  /** The one falsifiable claim we hold about why they are attending. */
  z.object({ kind: z.literal("attendanceHypothesis") }),
]);

export type VectorSlot = Readonly<z.infer<typeof VectorSlotSchema>>;

export const SlottedVectorSchema = z.object({
  slot: VectorSlotSchema,
  vector: z.array(z.number()),
});

/**
 * Every vector we hold for one person, from one embedding model at one size.
 *
 * A step of its own rather than fields on the brief because vectors are bulky
 * and nothing reads them the way a brief is read: a consumer wants one person's
 * whole set at once, and a brief change should not force a re-embed of text it
 * did not touch.
 *
 * `model` and `dimensions` ride on every row rather than living once on the run.
 * A vector without them cannot be compared with anything — two models, or two
 * sizes, do not share a space — so a row that travels alone has to say what it
 * is. That is also what makes projecting these into a vector index later a copy
 * rather than a join.
 *
 * On disk the vectors are BSON vectors (BinData subtype 9), which is both the
 * compact form and the one Atlas Vector Search indexes; the mapper decodes them
 * to plain numbers here so this type stays portable to the frontend.
 */
export const PersonEmbeddingsDataSchema = z.object({
  model: z.string(),
  dimensions: z.number().int(),
  vectors: z.array(SlottedVectorSchema),
});

export type PersonEmbeddingsData = Readonly<
  z.infer<typeof PersonEmbeddingsDataSchema>
>;

export const personEmbeddingsStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("personEmbeddings"),
  personId: PersonIdSchema,
  data: PersonEmbeddingsDataSchema.nullable(),
});

export type PersonEmbeddingsStepResult = Readonly<
  z.infer<typeof personEmbeddingsStepResultSchema>
>;

export const candidatePoolStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("candidatePool"),
  personId: PersonIdSchema,
  data: PersonPoolSchema.nullable(),
});

export type CandidatePoolStepResult = Readonly<
  z.infer<typeof candidatePoolStepResultSchema>
>;

/**
 * Per-person denormalized view of cluster membership. One doc per (run,
 * person): each carries only the clusters this person belongs to, with peer
 * members embedded. Downstream per-row cache reuse stays granular when one
 * input changes.
 */
export const clusterMembershipStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("clusterMembership"),
  personId: PersonIdSchema,
  data: ClusterMembershipDataSchema.nullable(),
});

export type ClusterMembershipStepResult = Readonly<
  z.infer<typeof clusterMembershipStepResultSchema>
>;

/**
 * The themes every lens designed, for the whole room. The one step-result that
 * names no person: a theme is a property of the event, not of anybody seated at
 * it, so there is a single doc per run rather than one per participant.
 */
export const clusterThemeDesignStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("clusterThemeDesign"),
  data: ClusterThemeDesignDataSchema.nullable(),
});

export type ClusterThemeDesignStepResult = Readonly<
  z.infer<typeof clusterThemeDesignStepResultSchema>
>;

/**
 * Base pick step-result — the S0→S3 snapshot for one participant. Subsequent overlay variants (pickRewrite, pickWhyConnectParagraph) express deltas applied on top; `foldPickState` composes them in order to reconstruct the current pick state.
 */
export const pickStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("pick"),
  personId: PersonIdSchema,
  data: PickSetSchema.nullable(),
});

export type PickStepResult = Readonly<z.infer<typeof pickStepResultSchema>>;

/**
 * S0 facet-selection sub-step. Per participant, the per-pair facet record: one entry per (candidate) selection with the facet directive the LLM produced (or `null` when the pair fell back to the deterministic-hint-only path because no preselection hints were available).
 */
export const pickMatchSelectionStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("pickMatchSelection"),
  personId: PersonIdSchema,
  data: z
    .object({
      selections: z.array(MatchSelectionSchema),
      substitutes: z.array(MatchSelectionSchema),
    })
    .nullable(),
});

export type PickMatchSelectionStepResult = Readonly<
  z.infer<typeof pickMatchSelectionStepResultSchema>
>;

/**
 * A gate's objection to one candidate, shaped like a pool `CandidateSignal`: a
 * `type` discriminant plus whatever evidence that gate specifically produces.
 * Adding a gate is adding a variant — no second list to update, and no consumer
 * has to be taught a new field.
 *
 * `score` sits on the base so every objection can be weighed against its
 * siblings without a per-type lookup table (the same reason the pool puts
 * `score` on its signal base). It is the gate's own declared objection strength,
 * 0–100; a binary gate declares 100. It is emphatically NOT a blended verdict —
 * a blended integer is exactly what left the render critic's grounding findings
 * unactionable. What the pipeline *does* about an objection (swap, repair,
 * merely record) is code-side policy applied when the gate starts enforcing, so
 * recalibrating it never rewrites history.
 */
const connectionGateBaseSchema = z.object({
  score: z.number(),
});

/**
 * Which line of the bilateral case an objection was quoted from — NOT which
 * person it is about. Those are not the same thing and the difference is a trap:
 * `readerGets` states what the reader gains FROM the candidate, so its factual
 * claims describe the candidate, while `candidateGets` describes the reader. It
 * also carries the odd conditional about its own subject, so no field-to-person
 * mapping is sound. Recording the field is provable; attributing a person is not,
 * so a reviewer checks the claim against both dossiers.
 */
const connectionGateFieldSchema = z.enum(["readerGets", "candidateGets"]);

export const ConnectionGateObjectionSchema = z.discriminatedUnion("type", [
  connectionGateBaseSchema.extend({
    type: z.literal("one_sided_value"),
    field: connectionGateFieldSchema,
    text: z.string(),
  }),
  connectionGateBaseSchema.extend({
    type: z.literal("ungrounded_claim"),
    field: connectionGateFieldSchema,
    // The numeric token the gate could not trace, verbatim from the case, so the
    // finding can be checked without re-deriving it.
    claim: z.string(),
  }),
  connectionGateBaseSchema.extend({
    type: z.literal("unsupported_reconnection"),
    // What S1 asserted, which is the thing being objected to. Recording the
    // absence of pool evidence instead would be a constant — this gate only
    // fires when there is none.
    claimedContext: z.string().nullable(),
  }),
]);

export type ConnectionGateObjection = z.infer<
  typeof ConnectionGateObjectionSchema
>;

/**
 * S1.5 connection-critique gate. One doc per participant with an entry for every
 * candidate S1 put in front of them, benched as well as active, so the doc holds
 * its own denominator. Keyed by candidate alone: the question is whether this
 * connection holds, and `portfolioRole` is S1's labelling of it — joinable from
 * `pickMatchSelection` when analysis wants it, and out of the way when it
 * doesn't.
 *
 * Only gates that object are recorded, so a candidate nothing objected to has an
 * empty `objections` array.
 *
 * Report-only: nothing reads this yet. `pickSlate` is unchanged and still
 * finalizes from S1's output alone.
 */
export const pickConnectionCritiqueStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("pickConnectionCritique"),
  personId: PersonIdSchema,
  data: z
    .object({
      candidates: z.array(
        z.object({
          candidatePersonId: PersonIdSchema,
          objections: z.array(ConnectionGateObjectionSchema),
        }),
      ),
    })
    .nullable(),
});

export type PickConnectionCritiqueStepResult = Readonly<
  z.infer<typeof pickConnectionCritiqueStepResultSchema>
>;

// Finalized connect slate (post-rebalance): the true picks and the
// benched substitutes. Emitted by connect so the finalization is observable;
// render consumes the picks and never reads the substitutes.
export const pickSlateStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("pickSlate"),
  personId: PersonIdSchema,
  data: z
    .object({
      picks: z.array(MatchSelectionSchema),
      substitutes: z.array(MatchSelectionSchema),
    })
    .nullable(),
});

export type PickSlateStepResult = Readonly<
  z.infer<typeof pickSlateStepResultSchema>
>;

/**
 * The prose arm of why-connect: one short paragraph per pick, applied as an overlay on
 * the base pick. It replaced the headline + bullets arm, whose step is gone (PLA-1359).
 */
const pickWhyConnectParagraphStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("pickWhyConnectParagraph"),
  personId: PersonIdSchema,
  data: z
    .object({
      /**
       * The language these paragraphs were written in. Stored plainly rather than
       * folded into `inputHash`: a discrete decision rather than payload, so salvage
       * guards on it before any hash, a mismatch explains itself, and the docs stay
       * inspectable. Optional on docs predating the field, where an unknown language
       * means "don't reuse".
       */
      outputLanguage: LocaleCodeSchema.optional(),
      picks: z.array(PickWhyConnectParagraphEntrySchema),
    })
    .nullable(),
});

export type PickWhyConnectParagraphStepResult = Readonly<
  z.infer<typeof pickWhyConnectParagraphStepResultSchema>
>;

/**
 * The `targetId` vocabulary of the paragraph arm. Persisted values, so they live with
 * the envelope rather than in either step: the writer, the judge and the read-time
 * fold all have to agree on which row a consumer reads, and a private constant on one
 * side of that is how a reader ends up served an attempt nobody chose (PLA-1359).
 *
 * A generation attempt carries its own id — absent for the base one — and the selected
 * winner is copied whole onto {@link PARAGRAPH_SELECTED_TARGET_ID}. Consumers read that
 * single row and never compare attempts, which is also what keeps a future prompt
 * variant free: it is one more attempt id, invisible downstream.
 */
export const PARAGRAPH_REWRITE_TARGET_ID = "rewrite-1";
export const PARAGRAPH_SELECTED_TARGET_ID = "selected";

/**
 * Judgement of an S6b paragraph. One doc per participant per generation attempt: the
 * envelope's `targetId` names the attempt, and its absence means the base one — the
 * same null-bucketing the S3.5 overlays use. A regeneration is therefore another doc,
 * not another field, and a future prompt *variant* is another doc too, so the arm
 * comparison and the rewrite loop are the same mechanism.
 *
 * Judgement lives here rather than in the generator that produced the paragraph. That
 * is deliberate: when the deterministic gate ran inside one step and the rubric ran
 * inside another, the gate ended up grading a draft that 87% of picks had already
 * replaced, and one field acquired two thresholds that drifted to 50 and 80 with
 * nobody owning either (PLA-1344). A generator cannot forget to be judged if it is
 * not the thing doing the judging — and a variant written by someone else inherits
 * the same judge by construction.
 *
 * Keyed by candidate alone. Which prompt wrote the paragraph is the attempt's
 * identity, not the candidate's, and the critic is deliberately blind to it: a judge
 * that knows which variant it is reading turns an arm comparison into a popularity
 * contest between prompts.
 */
const pickParagraphCritiqueStepResultSchema = z.object({
  ...envelopeBaseFields,
  type: z.literal("pickParagraphCritique"),
  personId: PersonIdSchema,
  data: z
    .object({
      candidates: z.array(
        z.object({
          candidatePersonId: PersonIdSchema,
          /**
           * Mechanical violations from `gradeProse`. Absent on the paragraph doc from
           * here on: the generator no longer stamps its own homework.
           */
          ruleFlags: z.array(z.string()),
          /**
           * Judged violations — grounding, hedging, reconnection, discretion. Nullish
           * on docs written before the LLM arm, and on any row where the call failed:
           * absent means unknown, never clean.
           */
          violations: z.array(z.string()).nullish(),
          /**
           * Verbatim spans of the paragraph backing those violations. A violation the
           * critic could not quote is dropped before it is persisted, so an entry here
           * with violations and no quotes cannot occur.
           */
          quotes: z.array(z.string()).nullish(),
          note: z.string().nullish(),
        }),
      ),
    })
    .nullable(),
});

export type PickParagraphCritiqueStepResult = Readonly<
  z.infer<typeof pickParagraphCritiqueStepResultSchema>
>;

export const MatchingEngineStepResultSchema = z
  .discriminatedUnion("type", [
    personCoreBriefStepResultSchema,
    markerParticipantStepResultSchema,
    personEmbeddingsStepResultSchema,
    candidatePoolStepResultSchema,
    clusterMembershipStepResultSchema,
    clusterThemeDesignStepResultSchema,
    pickStepResultSchema,
    pickMatchSelectionStepResultSchema,
    pickConnectionCritiqueStepResultSchema,
    pickSlateStepResultSchema,
    pickWhyConnectParagraphStepResultSchema,
    pickParagraphCritiqueStepResultSchema,
  ])
  .readonly();

export type MatchingEngineStepResult = z.infer<
  typeof MatchingEngineStepResultSchema
>;

/** Every variant whose document names no person. */
type PersonlessStepResultType = Exclude<
  MatchingEngineStepResult,
  { personId: PersonId }
>["type"];
